import { Component, DestroyRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReplaySubject, combineLatest, firstValueFrom } from 'rxjs';
import { ConversationListService } from '../../../../core/src/lib/state/conversation-list.service';
import { ConversationService } from '../../../../core/src/lib/state/conversation.service';
import { InboxNotificationService } from '../../../../core/src/lib/inbox-notification.service';
import { ALL_VIEW_ID } from '../../../../core/src/lib/inbox.constants';
import { ViewService } from '../../../../core/src/lib/view.service';
import { InboxService } from '../../../../core/src/lib/inbox.service';

const FOLLOW_VIEW_ID = 'following';

@Component({
  selector: 'inbox-views',
  templateUrl: './inbox-views.component.html',
  styleUrls: ['./inbox-views.component.scss'],
  standalone: false,
})
export class InboxViewsComponent {
  @Input() viewMode = false;
  readonly views$ = this.viewService.loadViews();
  selectedViewId$ = this.viewService.selectedViewId$;
  readonly allViewId = ALL_VIEW_ID;

  private readonly notificationWarningView$$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  readonly notificationWarningView$ = this.notificationWarningView$$.asObservable();

  constructor(
    private readonly router: Router,
    private readonly conversationService: ConversationService,
    private readonly inboxService: InboxService,
    private readonly conversationListService: ConversationListService,
    private readonly notificationsService: InboxNotificationService,
    private readonly viewService: ViewService,
    private readonly snackbarService: SnackbarService,
    private readonly destroyRef: DestroyRef,
  ) {}

  async showView(viewId: string): Promise<void> {
    const featureFlagFullscreen = await firstValueFrom(this.inboxService.featureFlagFullscreenInboxPC$);
    this.conversationService.removeLastConversationId();
    if (!featureFlagFullscreen) {
      this.router.navigate([{ outlets: { inbox: ['inbox', 'views', viewId, 'conversations'] } }], {
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate(['inbox', 'views', viewId, 'conversations'], {
        queryParamsHandling: 'merge',
      });
    }
    this.viewService.setViewId(viewId);
    this.conversationService.conversationSelected(false);
    this.notificationWarningBannerStatus(viewId);
  }

  notificationWarningBannerStatus(viewSection: string): void {
    combineLatest([
      this.notificationsService.inboxNotificationStatus$,
      this.conversationService.conversationSelected$,
      this.conversationListService.conversations$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([notificationSetting, conversationSelected, conversations]) => {
        const showNotificationWarningBanner =
          conversations && conversations.length > 0
            ? notificationSetting === false &&
              viewSection === FOLLOW_VIEW_ID &&
              this.notificationsService.getCookieStatus() === false &&
              conversationSelected === false
            : false;
        this.notificationWarningView$$.next(showNotificationWarningBanner);
      });
  }

  turnOnNotifications(): void {
    this.notificationsService
      .turnOnOnlyMyEventsNotification$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationWarningView$$.next(false);
          this.snackbarService.openSuccessSnack('INBOX.WARNING.NOTIFICATION_UPDATE_SUCCESS');
        },
        error: () => {
          this.snackbarService.openErrorSnack('INBOX.WARNING.NOTIFICATION_UPDATE_ERROR');
        },
      });
  }

  onClose(): void {
    this.notificationWarningView$$.next(false);
    this.notificationsService.setCookie();
  }
}
